import { useEffect, useState } from "react";
import { NavMenu } from "@shopify/app-bridge-react";

import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import ProductConfiguration from "./pages/ProductConfiguration";
import RentalTester from "./pages/RentalTester";
import RentalPage from "./pages/rental";
import RentalOrders from "./pages/RentalOrders";
import PendingApproval from "./pages/PendingApproval";
import RentalCalendar from "./pages/RentalCalendar";
import { getShopFromUrl } from "./utils/shop";

const PAGE_PATHS = {
  dashboard: "/",
  product: "/rental-options-configuration",
  rentalOrders: "/rental-orders",
  pendingApproval: "/pending-approval",
  calendar: "/calendar",
  rental: "/rental",
  tester: "/rental-tester",
};

function resolvePageFromPath(pathname) {
  if (pathname === PAGE_PATHS.product) return "product";
  if (pathname === PAGE_PATHS.rentalOrders) return "rentalOrders";
  if (pathname === PAGE_PATHS.pendingApproval) return "pendingApproval";
  if (pathname === PAGE_PATHS.calendar) return "calendar";
  if (pathname === PAGE_PATHS.rental) return "rental";
  if (pathname === PAGE_PATHS.tester) return "tester";
  return "dashboard";
}

export default function App() {
  const [page, setPage] = useState(() =>
    resolvePageFromPath(window.location.pathname),
  );

  const [license, setLicense] = useState(() => {
    try {
      const raw = localStorage.getItem("license");
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const appPath = String(params.get("appPath") || "").trim();
    if (!appPath) return;

    const normalizedPath = appPath.startsWith("/") ? appPath : `/${appPath}`;
    const allowedPaths = Object.values(PAGE_PATHS);
    if (!allowedPaths.includes(normalizedPath)) return;

    params.delete("appPath");
    const nextQuery = params.toString();
    const nextUrl = `${normalizedPath}${nextQuery ? `?${nextQuery}` : ""}`;

    if (`${window.location.pathname}${window.location.search}` !== nextUrl) {
      window.history.replaceState({}, "", nextUrl);
      setPage(resolvePageFromPath(normalizedPath));
    }
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      setPage(resolvePageFromPath(window.location.pathname));
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const navigateToPage = (nextPage) => {
    const nextPath = PAGE_PATHS[nextPage] || PAGE_PATHS.dashboard;
    const queryString = window.location.search || "";
    const nextUrl = `${nextPath}${queryString}`;

    if (`${window.location.pathname}${window.location.search}` !== nextUrl) {
      window.history.pushState({}, "", nextUrl);
    }

    setPage(nextPage);
  };

  useEffect(() => {
    async function loadLicense() {
      try {
        const shop = getShopFromUrl();
        const query = shop ? `?shop=${encodeURIComponent(shop)}` : "";
        const res = await fetch(`/api/admin/license${query}`, {
          credentials: "include",
        });

        if (!res.ok || res.status === 401) {
          localStorage.removeItem("license");
          setLicense(null);
          setLoading(false);
          return;
        }

        const data = await res.json();

        if (data && !data.error && data.licenseKey) {
          localStorage.setItem("license", JSON.stringify(data));
          setLicense(data);
        } else {
          localStorage.removeItem("license");
          setLicense(null);
        }
      } catch (err) {
        console.warn("License check failed:", err.message);
        localStorage.removeItem("license");
        setLicense(null);
      } finally {
        setLoading(false);
      }
    }

    loadLicense();
  }, []);

  useEffect(() => {
    const refreshLicense = async () => {
      try {
        const shop = getShopFromUrl();
        const query = shop ? `?shop=${encodeURIComponent(shop)}` : "";
        const res = await fetch(`/api/admin/license${query}`, {
          credentials: "include",
        });

        if (!res.ok || res.status === 401) {
          localStorage.removeItem("license");
          setLicense(null);
          return;
        }

        const data = await res.json();
        if (data && !data.error && data.licenseKey) {
          localStorage.setItem("license", JSON.stringify(data));
          setLicense(data);
        } else {
          localStorage.removeItem("license");
          setLicense(null);
        }
      } catch (_err) {
        // Keep current state if network is temporarily unreachable
      }
    };

    const intervalId = window.setInterval(refreshLicense, 15 * 1000);
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshLicense();
      }
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", refreshLicense);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", refreshLicense);
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("license");
    setLicense(null);
  };

  if (loading) {
    return <div style={{ padding: 20 }}>Loading...</div>;
  }

  if (!license) {
    return <Login onLoginSuccess={setLicense} />;
  }

  let view = <Dashboard license={license} onNavigate={navigateToPage} onLogout={handleLogout} />;

  if (page === "product") {
    view = <ProductConfiguration license={license} onNavigate={navigateToPage} />;
  }

  if (page === "rentalOrders") {
    view = <RentalOrders license={license} onNavigate={navigateToPage} />;
  }

  if (page === "pendingApproval") {
    view = <PendingApproval license={license} onNavigate={navigateToPage} />;
  }

  if (page === "calendar") {
    view = <RentalCalendar license={license} onNavigate={navigateToPage} />;
  }

  if (page === "rental") {
    view = <RentalPage onNavigate={navigateToPage} />;
  }

  if (page === "tester") {
    view = <RentalTester license={license} onNavigate={navigateToPage} />;
  }

  return (
    <>
      <NavMenu>
        <a href={`${PAGE_PATHS.dashboard}${window.location.search || ""}`} rel="home">
          Dashboard
        </a>
        <a href={`${PAGE_PATHS.product}${window.location.search || ""}`}>Rental Products</a>
        <a href={`${PAGE_PATHS.rentalOrders}${window.location.search || ""}`}>Rental Orders</a>
        <a href={`${PAGE_PATHS.pendingApproval}${window.location.search || ""}`}>Pending Approval</a>
        <a href={`${PAGE_PATHS.calendar}${window.location.search || ""}`}>Calendar</a>
      </NavMenu>
      {view}
    </>
  );
}
